import { isDefined } from '@aloft/toolkit-shared';

import { PropertyGridSection } from '../../../components/PropertyGrid/columnSections/PropertyGridSection.tsx';
import {
  getAloftAreaJSONPropertyGridField,
  getNumberPropertyGridField,
} from '../../../components/PropertyGrid/fields/utils.tsx';
import type {
  PropertyGridField,
  PropertyGridSectionProps,
} from '../../../components/PropertyGrid/types.ts';
import { getCondition, getQuality } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import {
  selectIncludeCostApproach,
  selectSalesComparisonApproach,
  selectShowCraftsmanQualityOnGrid,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsEntities } from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import {
  selectOrderHasExternalDepreciation,
  selectOrderHasFunctionalDepreciation,
} from '../../../redux/features/selectors/selectOrderDepreciation.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { ADJUSTMENT_LABELS } from '../../adjustments/consts.ts';
import type { ImprovementField } from './consts.ts';
import { improvementsFieldName } from './consts.ts';

const ONE_TO_SIX = [1, 2, 3, 4, 5, 6];
const qualityOptions = ONE_TO_SIX.map((n) => ({ label: `Q${n}`, value: n }));
const conditionOptions = ONE_TO_SIX.map((n) => ({ label: `C${n}`, value: n }));

export const AdjustmentsGridMainDwellingSection = (props: PropertyGridSectionProps) => {
  const orderHasFunctionalDepreciation = useAppSelector(selectOrderHasFunctionalDepreciation);
  const orderHasExternalDepreciation = useAppSelector(selectOrderHasExternalDepreciation);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const isUnitRate = useAppSelector(selectSalesComparisonApproach) === 'unit_rate';
  const improvementsEntities = useAppSelector(selectImprovementsEntities);
  const showCraftsmanQualityOnGrid = useAppSelector(selectShowCraftsmanQualityOnGrid);

  const qualityField: PropertyGridField<number> = {
    fieldName: 'editableData.improvements.uadQuality',
    label: ADJUSTMENT_LABELS.quality,
    manualAdjustmentKey: 'quality',
    options: qualityOptions,
    unitRateKey: isUnitRate ? 'quality' : undefined,
    unit: isUnitRate ? 'rating' : undefined,
    toTriggerLabel: (uadQuality, propertyId) => {
      const improvements = improvementsEntities[propertyId];
      return includeCostApproach
        ? getQuality(uadQuality, improvements?.craftsmanQuality)
        : `Q${uadQuality ?? '-'}`;
    },
  };

  const conditionField: PropertyGridField<number> = {
    fieldName: 'editableData.improvements.uadCondition',
    label: ADJUSTMENT_LABELS.condition,
    manualAdjustmentKey: 'condition',
    options: conditionOptions,
    unitRateKey: isUnitRate ? 'condition' : undefined,
    unit: isUnitRate ? 'rating' : undefined,
    toTriggerLabel: (uadCondition, propertyId) => {
      const improvements = improvementsEntities[propertyId];
      return includeCostApproach
        ? getCondition(uadCondition, improvements?.effectiveAge)
        : `C${uadCondition ?? '-'}`;
    },
  };

  const halfBathCountField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.halfBaths,
    manualAdjustmentKey: 'halfBaths',
    unitRateKey: 'halfBaths',
    unit: 'ba',
    toDisplayValue: (improvements) =>
      isDefined(improvements?.halfBaths) ? `${improvements.halfBaths ?? 0}` : undefined,
  };

  const fullBathCountField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.fullBaths,
    manualAdjustmentKey: 'fullBaths',
    unitRateKey: 'fullBaths',
    unit: 'ba',
    toDisplayValue: (improvements) =>
      isDefined(improvements?.fullBaths) ? `${improvements.fullBaths ?? 0}` : undefined,
  };

  const bathAboveGradeField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.bathAboveGrade,
    manualAdjustmentKey: 'bathAboveGrade',
    toDisplayValue: (improvements) => {
      if (!isDefined(improvements?.fullBaths) && !isDefined(improvements?.halfBaths)) {
        // eslint-disable-next-line unicorn/no-useless-undefined
        return undefined;
      }
      return `${improvements?.fullBaths ?? 0}.${improvements?.halfBaths ?? 0}`;
    },
  };

  const depreciationFields: ImprovementField[] = [];

  if (orderHasFunctionalDepreciation) {
    depreciationFields.push({
      fieldName: improvementsFieldName,
      label: ADJUSTMENT_LABELS.functionalUtility,
      manualAdjustmentKey: 'functionalUtility',
      // eslint-disable-next-line unicorn/no-useless-undefined
      toDisplayValue: () => undefined,
    });
  }

  if (orderHasExternalDepreciation) {
    depreciationFields.push({
      fieldName: improvementsFieldName,
      label: ADJUSTMENT_LABELS.externalObsolescence,
      manualAdjustmentKey: 'externalObsolescence',
      // eslint-disable-next-line unicorn/no-useless-undefined
      toDisplayValue: () => undefined,
    });
  }

  return (
    <PropertyGridSection
      {...props}
      title="Main Dwelling"
      hasSeparateArvValue
      fields={[
        ...(isUnitRate
          ? [
              getNumberPropertyGridField({
                fieldName: 'editableData.improvements.age',
                label: ADJUSTMENT_LABELS.actualAge,
                manualAdjustmentKey: 'actualAge',
                unitRateKey: 'actualAge',
                unit: 'yr',
              }),
            ]
          : []),
        qualityField,
        ...(includeCostApproach && showCraftsmanQualityOnGrid
          ? [
              getNumberPropertyGridField({
                fieldName: 'editableData.improvements.craftsmanQuality',
                label: 'Craftsman Quality',
                manualAdjustmentKey: 'quality',
                toDisplayValue: (value) => (isDefined(value) ? value.toFixed(1) : undefined),
              }),
            ]
          : []),
        conditionField,
        getAloftAreaJSONPropertyGridField({
          fieldName: 'editableData.improvements.gla',
          label: 'GLA',
          manualAdjustmentKey: 'gla',
          unitRateKey: 'mainDwelling',
          unit: 'sf',
        }),
        getNumberPropertyGridField({
          fieldName: 'editableData.improvements.bedrooms',
          label: 'Bed Count',
          manualAdjustmentKey: 'bedrooms',
          unitRateKey: 'bedrooms',
          unit: 'bed',
        }),
        ...(isUnitRate ? [fullBathCountField, halfBathCountField] : [bathAboveGradeField]),
        ...depreciationFields,
      ]}
    />
  );
};
